<template>
  <div class="publications-table">
    <PublicationModal ref="modal" @row-updated="updateRow" @row-added="rowAdded" />

    <AgGridWrapper
      v-if="columnDefs"
      ref="agGrid"
      :options="gridOptions"
      :actions="tableActions"
      :infinite-scroll-request="infiniteScrollRequest"
      :visible-columns="visibleColumns"
      :update-rows-count="updateRowsCount"
      class="fit"
      title="Publications"
      handle-update-on-parent
      has-custom-column-filter
      has-export-excel
      @export-to-excel="mixinExportToExcel($event, [], `title-lists/${$route.params.id}`, null, 'actions')"
      @cell-updated="cellUpdated"
      @add-new="onAddNewClick"
    >
      <template #footerActions>
        <div class="row fit q-pr-md q-pb-md justify-end">Total Rows: {{ totalRows }}</div>
      </template>
    </AgGridWrapper>

    <div v-else class="loader-wrapper fit flex justify-center items-center">
      <q-spinner color="primary" size="3.5em" />
    </div>
  </div>
</template>

<script>
import * as apiTo from '../../api'
import { dateRenderer, dateSorter } from '@/lib/generic.utils'
import { exportToExcel } from '@/mixins/export-to-excel'
// Components
import AgGridWrapper from '@components/ag-grid-helpers/AgGridWrapper'
import LmEntityActions from '@components/ag-grid-helpers/EntityActions'
import PublicationModal from './PublicationModal'
import { mapMutations } from 'vuex'

export default {
  components: {
    AgGridWrapper,
    PublicationModal
  },

  mixins: [exportToExcel],

  data: () => ({
    tableCols: null,
    totalRows: null,
    showModal: false,
    visibleColumns: ['publication_title', 'print_identifier', 'online_identifier']
  }),

  computed: {
    tableActions() {
      return [{ label: 'Add new', type: 'button', color: 'tertiary', event: 'add-new' }]
    },

    columnDefs() {
      if (!this.tableCols) return
      const colDefs = this.tableCols.map((col, index) => {
        const colDef = this.getBaseColDef(col, index > 2)

        if (col.data_type === 'date') {
          colDef.cellEditor = 'dateCellEditor'
          colDef.comparator = dateSorter
        }
        return colDef
      })

      colDefs.push({
        headerName: 'Actions',
        field: 'actions',
        filter: false,
        lockVisible: true,
        cellRendererFramework: LmEntityActions
      })

      return colDefs
    },

    gridOptions() {
      return {
        context: {
          sortColumn: 0,
          actions: [
            {
              label: 'Edit',
              onClick: this.onEdit,
              color: 'tertiary'
            },
            {
              icon: 'trash',
              tooltip: 'Delete',
              onClick: this.delete,
              color: 'faded'
            }
          ]
        },
        defaultColDef: {
          floatingFilter: true,
          filter: 'agTextColumnFilter',
          suppressMenu: true,
          floatingFilterComponentParams: {
            debounceMs: 800
          }
        },
        columnDefs: this.columnDefs,
        cacheBlockSize: 40,
        pagination: false,
        rowModelType: 'serverSide',
        serverSideStoreType: 'partial',
        domLayout: 'normal'
      }
    }
  },

  created() {
    this.getTableCols()
  },

  methods: {
    ...mapMutations('title-list-show', ['increasePublicationsCount', 'decreasePublicationsCount']),

    getTableCols() {
      apiTo.getTableCols().then(({ data }) => (this.tableCols = data.result))
    },

    infiniteScrollRequest(request) {
      return apiTo.getPublications(this.$route.params.id, request)
    },

    updateRowsCount(rowsCount) {
      this.totalRows = rowsCount
    },

    getBaseColDef(col, hide) {
      return {
        headerName: col.label,
        field: col.name,
        editable: true,
        cellRenderer: params =>
          col.data_type === 'date'
            ? `<span>${dateRenderer(params.value)}</span>`
            : `<span>${params.value ? params.value : 'Insert...'}</span>`,
        tooltipValueGetter: params =>
          col.data_type === 'date' ? `${dateRenderer(params.value)}` : `${params.value ? params.value : 'Insert...'}`,
        hide
      }
    },

    cellUpdated({ newValue, oldValue, colDef: { field }, data }) {
      if (newValue === oldValue) return
      apiTo.updatePublication(data.id, data).catch(() => {
        this.$refs.agGrid.updateRowNode(data.id, { ...data, ...{ [field]: oldValue } })
      })
    },

    onEdit({ data: rowData }) {
      this.$refs.modal.open(this.tableCols, rowData)
    },

    onAddNewClick() {
      this.$refs.modal.open(this.tableCols)
    },

    updateRow(updatedRow) {
      this.$refs.agGrid.updateRowNode(updatedRow.id, updatedRow)
    },

    delete({ data: { id } }) {
      apiTo.deletePublication(id).then(() => {
        this.decreasePublicationsCount()
        this.$refs.agGrid.purgeServerSideCache()
      })
    },

    rowAdded() {
      this.increasePublicationsCount()
      this.$refs.agGrid.purgeServerSideCache()
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/styles/stylus/mixins'

>>> .table-content {
  height calc(100% - 90px) !important
  padding 0 40px !important
}

>>> .ag-theme-material {
  height 100%
}

styledScrollFor('>>> .ag-body-viewport.ag-layout-normal');

.publications-table {
  height 100%
}
</style>
